import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { AlertCircle, Download, CheckCircle2, Upload, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { ropaAPI } from '../../../../services/api';

/**
 * CSV Import Dialog Component
 * Three-stage workflow: Upload → Preview → Result
 */
export function CSVImportDialog({ open, onOpenChange, onComplete }) {
  const [stage, setStage] = useState('upload'); // 'upload' | 'preview' | 'result'
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await ropaAPI.downloadTemplate();
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ropa-import-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setLoading(true);
      const response = await ropaAPI.previewImport(selectedFile);
      setPreview(response.data);
      setStage('preview');
    } catch (error) {
      console.error('Failed to preview import:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setLoading(true);
      const response = await ropaAPI.confirmImport(selectedFile);
      setResult(response.data);
      setStage('result');
    } catch (error) {
      console.error('Failed to confirm import:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStage('upload');
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    onOpenChange(false);
    if (stage === 'result') {
      onComplete?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import ROPA Records from CSV</DialogTitle>
          <DialogDescription>
            {stage === 'upload' && 'Upload a CSV file with ROPA record data'}
            {stage === 'preview' && 'Review the data before importing'}
            {stage === 'result' && 'Import completed'}
          </DialogDescription>
        </DialogHeader>

        {/* Stage 1: Upload */}
        {stage === 'upload' && (
          <div className="space-y-6">
            <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Step 1: Download Template
                </CardTitle>
                <CardDescription>
                  Start with our template to ensure correct column format
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV Template
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Step 2: Upload Your File
                </CardTitle>
                <CardDescription>
                  Select your completed CSV file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="flex-1"
                    />
                  </div>
                  {selectedFile && (
                    <div className="text-sm text-muted-foreground">
                      Selected: <span className="font-medium">{selectedFile.name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Step 3: Preview Data
                </CardTitle>
                <CardDescription>
                  Review your data before importing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handlePreview}
                  disabled={!selectedFile || loading}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {loading ? 'Previewing...' : 'Preview CSV'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stage 2: Preview */}
        {stage === 'preview' && preview && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Rows Parsed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{preview.rows_parsed}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Valid Rows</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{preview.rows_valid}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Invalid Rows</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{preview.rows_invalid}</div>
                </CardContent>
              </Card>
            </div>

            {/* Errors */}
            {preview.errors && preview.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Validation Errors</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
                    {preview.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>
                        Row {error.row}: {error.field} — {error.message}
                      </li>
                    ))}
                    {preview.errors.length > 5 && (
                      <li className="text-xs text-muted-foreground">
                        ... and {preview.errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preview (First 10 Rows)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-semibold">Activity Name</th>
                        <th className="text-left py-2 px-2 font-semibold">Entity</th>
                        <th className="text-left py-2 px-2 font-semibold">Department</th>
                        <th className="text-left py-2 px-2 font-semibold">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50">
                          <td className="py-2 px-2 text-xs">{row.process_name}</td>
                          <td className="py-2 px-2 text-xs">{row.entity_name}</td>
                          <td className="py-2 px-2 text-xs">{row.department}</td>
                          <td className="py-2 px-2 text-xs">{row.activity_name?.substring(0, 30)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setStage('upload')}
              >
                Back
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={preview.rows_valid === 0 || loading}
              >
                {loading ? 'Importing...' : `Import ${preview.rows_valid} Records`}
              </Button>
            </div>
          </div>
        )}

        {/* Stage 3: Result */}
        {stage === 'result' && result && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Import Completed</h3>
              <p className="text-muted-foreground">
                Your ROPA records have been imported successfully.
              </p>
            </div>

            <Card className="bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-green-600">{result.created}</div>
                    <div className="text-sm text-muted-foreground">Records Created</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-amber-600">{result.skipped}</div>
                    <div className="text-sm text-muted-foreground">Skipped</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-900 dark:text-green-400">
                All records have been imported as <strong>Draft</strong>
              </AlertTitle>
              <AlertDescription className="text-green-800 dark:text-green-300">
                You can review and edit them in the ROPA Records list before publishing.
              </AlertDescription>
            </Alert>

            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
